import { useEffect, useRef, useState } from 'react'
import type { RunPhase } from '@/lib/types'

// The four run phases, in order, with the label shown in the rail beneath the grid.
const PHASES: { phase: RunPhase; label: string }[] = [
  { phase: 'generating', label: 'Mapping intents' },
  { phase: 'querying', label: 'Querying the engines' },
  { phase: 'analyzing', label: 'Reading answers' },
  { phase: 'scoring', label: 'Scoring visibility' },
]

const ORDER: RunPhase[] = ['generating', 'querying', 'analyzing', 'scoring']

// Keep the grid legible: past this many probes we scale the visual grid down and
// map progress onto it proportionally (the number still shows the true totals).
const MAX_CELLS = 240

// prefers-reduced-motion — kill the count-up + shimmer when set.
function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false)

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReduced(mq.matches)

    const onChange = () => setReduced(mq.matches)
    mq.addEventListener('change', onChange)

    return () => mq.removeEventListener('change', onChange)
  }, [])

  return reduced
}

// Smoothly animate a displayed integer toward `target` (ease-out). Snaps instantly
// under reduced motion. The count-up is what makes the number feel alive.
function useCountUp(target: number, reduced: boolean): number {
  const [display, setDisplay] = useState(target)
  const fromRef = useRef(target)
  const rafRef = useRef<number | undefined>(undefined)

  useEffect(() => {
    if (reduced) {
      setDisplay(target)
      return
    }

    const from = fromRef.current
    const delta = target - from
    if (delta === 0) return

    const durationMs = 600
    let start: number | undefined

    const tick = (t: number) => {
      if (start === undefined) start = t
      const p = Math.min(1, (t - start) / durationMs)
      const eased = 1 - (1 - p) ** 3 // ease-out cubic

      setDisplay(Math.round(from + delta * eased))

      if (p < 1) {
        rafRef.current = requestAnimationFrame(tick)
      } else {
        fromRef.current = target
      }
    }

    rafRef.current = requestAnimationFrame(tick)

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      fromRef.current = target
    }
  }, [target, reduced])

  return display
}

export interface RunProgressProps {
  phase?: RunPhase
  cellsDone?: number
  cellsTotal?: number
}

export function RunProgress({
  phase,
  cellsDone = 0,
  cellsTotal = 0,
}: RunProgressProps) {
  const reduced = useReducedMotion()

  const phaseIndex = phase && phase !== 'done' ? ORDER.indexOf(phase) : -1

  // The querying fan-out drives the grid. Before it starts (generating) we show
  // a gentle pre-warm; the number reflects the true probe totals throughout.
  const hasGrid = cellsTotal > 0
  const done = Math.min(cellsDone, cellsTotal)
  const count = useCountUp(done, reduced)
  const pct = cellsTotal > 0 ? Math.round((done / cellsTotal) * 100) : 0

  // Visual grid: one cell per probe, scaled down past MAX_CELLS. Map the real
  // done-count onto the (possibly scaled) cell array.
  const cellCount = Math.min(cellsTotal || 60, MAX_CELLS)
  const filledCells =
    cellsTotal > 0 ? Math.round((done / cellsTotal) * cellCount) : 0

  // The "frontier" — the handful of cells just past the fill line that shimmer,
  // reading as the live edge of the fan-out.
  const FRONTIER = 5

  return (
    <div className="mx-auto mt-12 max-w-2xl">
      {/* Status eyebrow with a breathing dot. */}
      <div className="mb-8 flex items-center justify-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
        <span className="relative flex size-2">
          {!reduced && (
            <span className="absolute inline-flex size-full animate-ping rounded-full bg-primary opacity-60" />
          )}
          <span className="relative inline-flex size-2 rounded-full bg-primary" />
        </span>
        Live
      </div>

      {/* Hero: the probe count, animating up. */}
      <div className="text-center">
        <div className="flex items-end justify-center gap-3">
          <span className="bg-gradient-to-b from-foreground to-foreground/60 bg-clip-text text-7xl font-bold leading-none tracking-tight text-transparent tabular-nums sm:text-8xl">
            {hasGrid ? count : '···'}
          </span>
          {hasGrid && (
            <span className="mb-2 text-lg text-muted-foreground tabular-nums">
              / {cellsTotal}
            </span>
          )}
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          {hasGrid
            ? `AI answers gathered${pct > 0 ? ` · ${pct}%` : ''}`
            : 'Preparing your probes…'}
        </p>
      </div>

      {/* Signal grid — the fan-out made visible. Each cell is one probe. */}
      <div
        className="mt-8 grid justify-center gap-1"
        style={{
          gridTemplateColumns: 'repeat(auto-fill, 14px)',
          maxWidth: '30rem',
          marginInline: 'auto',
        }}
        aria-hidden="true"
      >
        {Array.from({ length: cellCount }, (_, i) => {
          const isFilled = i < filledCells
          const isFrontier =
            !isFilled && i < filledCells + FRONTIER && phase === 'querying'

          return (
            <span
              key={i}
              className={`size-3.5 rounded-[3px] transition-colors duration-500 ${
                isFilled
                  ? 'bg-primary'
                  : isFrontier
                    ? 'bg-primary/40'
                    : 'bg-muted'
              } ${isFrontier && !reduced ? 'animate-pulse' : ''}`}
            />
          )
        })}
      </div>

      {/* Phase rail: the four stages as a slim connected track. */}
      <ol className="mt-10 flex items-center justify-center gap-1 text-xs">
        {PHASES.map((p, i) => {
          const state =
            phase === 'done' || (phaseIndex >= 0 && i < phaseIndex)
              ? 'done'
              : i === phaseIndex
                ? 'active'
                : 'pending'

          return (
            <li key={p.phase} className="flex items-center gap-1">
              {i > 0 && (
                <span
                  className={`h-px w-4 sm:w-8 ${state === 'pending' ? 'bg-border' : 'bg-primary/50'}`}
                />
              )}
              <span
                className={`flex items-center gap-1.5 rounded-full px-2 py-1 transition ${
                  state === 'active'
                    ? 'bg-primary/10 font-medium text-primary'
                    : state === 'done'
                      ? 'text-foreground/70'
                      : 'text-muted-foreground'
                }`}
              >
                <span
                  className={`size-1.5 rounded-full ${
                    state === 'active'
                      ? 'bg-primary'
                      : state === 'done'
                        ? 'bg-emerald-500'
                        : 'bg-muted-foreground/30'
                  }`}
                />
                <span className="hidden sm:inline">{p.label}</span>
              </span>
            </li>
          )
        })}
      </ol>
    </div>
  )
}
